import { USER_PERMISSIONS } from "@hyro/dashboard-permissions";
import {
  BuildOutlined as BuildOutlinedIcon,
  ExploreOutlined as ExploreOutlinedIcon,
  OfflineBoltOutlined as OfflineBoltOutlinedIcon,
  SettingsOutlined as SettingsOutlinedIcon,
  TransformOutlined as TransformOutlinedIcon
} from "@mui/icons-material";

import AssistantIcon from "../../icons/AssistantIcon";
import BranchDeployerIcon from "../../icons/BranchDeployerIcon";
import CalendarNavIcon from "../../icons/CalendarNavIcon";
import ClockIconMenu from "../../icons/ClockIconMenu";
import CloudCacheIcon from "../../icons/CloudCache";
import ConversationsIcon from "../../icons/ConversationsIcon";
import DashboardSettingsIcon from "../../icons/DashboardSettingsIcon";
import EditorIcon from "../../icons/EditorIcon";
import FolderIcon from "../../icons/FolderIcon";
import GridIcon from "../../icons/GridIcon";
import InsightsIcon from "../../icons/InsightsIcon";
import IntegrationIcon from "../../icons/IntegrationIcon";
import KnowledgeStudioIcon from "../../icons/KnowledgeStudioIcon";
import OutboundIcon from "../../icons/OutboundIcon";
import PieChartIcon from "../../icons/PieChartIcon";
import SentimentAnalysisIcon from "../../icons/SentimentAnalysisIcon";
import {
  ACCOUNT_MANAGEMENT,
  ACCOUNT_SETTINGS,
  ALL_ASSISTANTS,
  AUDIT_LOG, BACK_OFFICE,
  BRANCH_DEPLOYER,
  BUILDING_BLOCKS,
  CHAT_INSIGHTS,
  CONVERSATIONS,
  CORE_HYRO_KPIS,
  ENVIRONMENTS,
  HOME,
  INSIGHTS,
  INTEGRATIONS,
  KNOWLEDGE_DEBUGGER,
  KNOWLEDGE_EXPLORER,
  KNOWLEDGE_SOURCES,
  PIPELINE_PERFORMANCE,
  AGENT_PERFORMANCE,
  SYSTEM_OVERRIDES,
  SKILLS,
  VOICE_INSIGHTS,
  CAMPAIGN_MANAGER,
  DATA_PROCESSING,
  CLINIC_AVAILABILITY,
  DRAFTS,
  FAILURES,
  CLOUD_CACHE_UPDATE,
  DATA_MAPPING,
  CONFIGURATION_EDITOR,
  ASSISTANTS_FEATURES,
  AGENT_SETTINGS,
  DATA_AGENT,
  SENTIMENT_ANALYSIS
} from "@consts";

const CONVERSATIONS_MIN = import.meta.env.VITE_ENV === ENVIRONMENTS.PRODUCTION ? "minute" : "minute";

const filterNavItemByPermission = hasPermission => (navItem) => {
  if (navItem.children) {
    navItem.children = navItem.children.filter(filterNavItemByPermission(hasPermission));
  }
  return navItem.requiredPermission
    ? hasPermission(navItem.requiredPermission)
    : true;
};

const getAssistantNavSections = (accountId, selectedAssistantId) => [
  {
    key: "analytics",
    title: "Analytics",
    items: [
      {
        key: HOME,
        text: "Overview",
        icon: PieChartIcon,
        url: `/${accountId}/${selectedAssistantId}/${HOME}`,
        requiredPermission: USER_PERMISSIONS.HOME_PAGE.READ
      },
      {
        key: SENTIMENT_ANALYSIS,
        text: "Sentiment Analysis",
        icon: SentimentAnalysisIcon,
        url: `/${accountId}/${selectedAssistantId}/${SENTIMENT_ANALYSIS}`,
        requiredPermission: USER_PERMISSIONS.SENTIMENT_ANALYSIS_PAGE.READ,
        requiredFlag: "enableSentimentAnalysis",
        voiceOnly: true
      },
      {
        key: INSIGHTS,
        text: "Insights",
        description: `Loading data may take time, it refreshes every ${CONVERSATIONS_MIN}.`,
        url: `/${accountId}/${selectedAssistantId}/${INSIGHTS}`,
        icon: InsightsIcon,
        requiredPermission: USER_PERMISSIONS.INSIGHTS_PAGE.READ
      },
      {
        key: CONVERSATIONS,
        text: "Conversations",
        description: `The data is refreshed every ${CONVERSATIONS_MIN}.`,
        icon: ConversationsIcon,
        url: `/${accountId}/${selectedAssistantId}/${CONVERSATIONS}`,
        requiredPermission: USER_PERMISSIONS.CONVERSATIONS_PAGE.READ
      }
    ]
  },
  {
    key: "configuration",
    title: "Configuration",
    items: [
      {
        key: SKILLS,
        text: "Skills",
        icon: OfflineBoltOutlinedIcon,
        url: `/${accountId}/${selectedAssistantId}/${SKILLS}`,
        requiredPermission: USER_PERMISSIONS.SKILLS_PAGE.READ,
        isCodelessRoute: true
      },
      {
        key: BUILDING_BLOCKS,
        text: "Building Blocks",
        icon: GridIcon,
        url: `/${accountId}/${selectedAssistantId}/${BUILDING_BLOCKS}`,
        requiredPermission: USER_PERMISSIONS.BUILDING_BLOCKS_PAGE.READ,
        isCodelessRoute: true
      },
      {
        key: INTEGRATIONS,
        text: "Integrations",
        icon: IntegrationIcon,
        url: `/${accountId}/${selectedAssistantId}/${INTEGRATIONS}`,
        requiredPermission: USER_PERMISSIONS.INTEGRATIONS_PAGE.READ,
        isCodelessRoute: true
      },
      {
        key: KNOWLEDGE_SOURCES,
        text: "Knowledge Studio",
        icon: KnowledgeStudioIcon,
        url: `/${accountId}/${selectedAssistantId}/${KNOWLEDGE_SOURCES}`,
        requiredPermission: USER_PERMISSIONS.KNOWLEDGE_SOURCES_PAGE.READ,
        isCodelessRoute: true
      },
      {
        key: CAMPAIGN_MANAGER,
        text: "Campaign Manager",
        icon: OutboundIcon,
        url: `/${accountId}/${selectedAssistantId}/${CAMPAIGN_MANAGER}`
      },
      {
        key: CLINIC_AVAILABILITY,
        text: "Clinic Availability",
        icon: CalendarNavIcon,
        url: `/${accountId}/${selectedAssistantId}/${CLINIC_AVAILABILITY}`,
        requiredPermission: [
          USER_PERMISSIONS.CLINIC_AVAILABILITY.SUPERVISOR.READ,
          USER_PERMISSIONS.CLINIC_AVAILABILITY.MANAGER.READ
        ],
        requiredFlag: "clinicAvailabilityPage",
        voiceOnly: true
      }
    ]
  },
  {
    key: "hyro-tools",
    title: "Hyro Tools",
    requiredPermission: USER_PERMISSIONS.HYRO_TOOLS_SECTION.READ,
    items: [
      {
        key: AGENT_SETTINGS,
        text: "Agent Settings",
        icon: SettingsOutlinedIcon,
        url: `/${accountId}/${selectedAssistantId}/${AGENT_SETTINGS}`
      },
      {
        key: KNOWLEDGE_EXPLORER,
        text: "Knowledge Explorer",
        icon: ExploreOutlinedIcon,
        url: `/${accountId}/${selectedAssistantId}/${KNOWLEDGE_EXPLORER}`,
        requiredPermission: USER_PERMISSIONS.KNOWLEDGE_EXPLORER_PAGE.READ
      },
      {
        key: CLOUD_CACHE_UPDATE,
        text: "Cloud Cache Editor",
        icon: CloudCacheIcon,
        url: `/${accountId}/${selectedAssistantId}/${CLOUD_CACHE_UPDATE}`,
        requiredPermission: USER_PERMISSIONS.CLOUD_CACHE_EDITOR_PAGE.READ
      },
      {
        key: CONFIGURATION_EDITOR,
        text: "Configuration Editor",
        icon: EditorIcon,
        url: `/${accountId}/${selectedAssistantId}/${CONFIGURATION_EDITOR}`,
        requiredPermission: USER_PERMISSIONS.CONFIGURATION_EDITOR_PAGE.READ
      },
      {
        key: AUDIT_LOG,
        text: "Audit Log",
        icon: ClockIconMenu,
        url: `/${accountId}/${selectedAssistantId}/${AUDIT_LOG}`,
        requiredPermission: USER_PERMISSIONS.AUDIT_LOG.READ
      }
    ]
  }
];

const getAdminNavItems = () => [
  {
    key: ALL_ASSISTANTS,
    text: "All Agents",
    icon: AssistantIcon,
    url: `/admin/${ALL_ASSISTANTS}`
  },
  {
    key: ACCOUNT_MANAGEMENT,
    text: "Account Management",
    icon: FolderIcon,
    url: `/admin/${ACCOUNT_MANAGEMENT}`
  },
  {
    key: BRANCH_DEPLOYER,
    text: "Branch Deployer",
    icon: BranchDeployerIcon,
    url: `/admin/${BRANCH_DEPLOYER}`,
    requiredPermission: USER_PERMISSIONS.ADMIN_PANEL.MULTI_SERVICES.READ
  },
  {
    key: INSIGHTS,
    text: "Insights",
    icon: InsightsIcon,
    children: [
      {
        key: `${INSIGHTS}/${VOICE_INSIGHTS}`,
        text: "Voice",
        url: `/admin/insights/${VOICE_INSIGHTS}`
      },
      {
        key: `${INSIGHTS}/${CHAT_INSIGHTS}`,
        text: "Chat",
        url: `/admin/insights/${CHAT_INSIGHTS}`
      },
      {
        key: `${INSIGHTS}/${PIPELINE_PERFORMANCE}`,
        text: "Pipeline Performance",
        url: `/admin/insights/${PIPELINE_PERFORMANCE}`
      },
      {
        key: `${INSIGHTS}/${CORE_HYRO_KPIS}`,
        text: "Core Hyro KPIs",
        url: `/admin/insights/${CORE_HYRO_KPIS}`
      },
      {
        key: `${INSIGHTS}/${AGENT_PERFORMANCE}`,
        text: "Agent Performance",
        url: `/admin/insights/${AGENT_PERFORMANCE}`
      }
    ]
  },
  {
    key: BACK_OFFICE,
    text: "Back Office",
    icon: BuildOutlinedIcon,
    children: [
      {
        key: `${BACK_OFFICE}/${DRAFTS}`,
        text: "Drafts",
        url: `/admin/back-office/${DRAFTS}`
      },
      {
        key: `${BACK_OFFICE}/${FAILURES}`,
        text: "Failures",
        url: `/admin/back-office/${FAILURES}`
      },
      {
        key: `${BACK_OFFICE}/${KNOWLEDGE_DEBUGGER}`,
        text: "Debugger",
        url: `/admin/back-office/${KNOWLEDGE_DEBUGGER}`
      },
      {
        key: `${BACK_OFFICE}/${KNOWLEDGE_EXPLORER}`,
        text: "Explorer",
        url: `/admin/back-office/${KNOWLEDGE_EXPLORER}`
      },
      {
        key: `${BACK_OFFICE}/${DATA_MAPPING}`,
        text: "Data Mapping",
        url: `/admin/back-office/${DATA_MAPPING}`,
        requiredPermission: USER_PERMISSIONS.DATA_MAPPING.READ,
      },
      {
        key: `${BACK_OFFICE}/${ASSISTANTS_FEATURES}`,
        text: "Agents Features",
        url: `/admin/back-office/${ASSISTANTS_FEATURES}`
      },
      {
        key: `${BACK_OFFICE}/${DATA_AGENT}`,
        text: "Data Agent",
        url: `/admin/back-office/${DATA_AGENT}`
      }
    ]
  },
  {
    key: AUDIT_LOG,
    text: "Audit Log",
    icon: ClockIconMenu,
    url: `/admin/${AUDIT_LOG}`,
    requiredPermission: USER_PERMISSIONS.AUDIT_LOG.READ
  },
  {
    key: SYSTEM_OVERRIDES,
    text: "System Overrides",
    icon: DashboardSettingsIcon,
    url: `/admin/${SYSTEM_OVERRIDES}`
  }
];

const getAccountNavItems = (accountId) => [
  {
    key: ALL_ASSISTANTS,
    text: "All Agents",
    icon: AssistantIcon,
    url: `/${accountId}`,
    requiredPermission: USER_PERMISSIONS.ACCOUNT_ASSISTANTS_PAGE.READ
  },
  {
    key: CAMPAIGN_MANAGER,
    text: "Campaign Manager",
    icon: OutboundIcon,
    url: `/${accountId}/${CAMPAIGN_MANAGER}`,
    requiredPermission: USER_PERMISSIONS.CAMPAIGN_MANAGER.READ
  },
  {
    key: DATA_PROCESSING,
    text: "Data Processing",
    icon: TransformOutlinedIcon,
    url: `/${accountId}/${DATA_PROCESSING}`,
    requiredPermission: USER_PERMISSIONS.CAMPAIGN_MANAGER.READ,
    requiredFlag: "dataProcessingPage"
  },
  {
    key: ACCOUNT_SETTINGS,
    text: "Account Settings",
    icon: SettingsOutlinedIcon,
    url: `/${accountId}/${ACCOUNT_SETTINGS}`,
    requiredPermission: USER_PERMISSIONS.ACCOUNT_SETTINGS.READ
  }
];

export {
  filterNavItemByPermission,
  getAssistantNavSections,
  getAdminNavItems,
  getAccountNavItems
};
